import { Match } from "model/Match/Match";
import {
  getDriverIdsFromRosterIds,
  getRoster,
  getRosterIdFromDriverId,
  isForfeited,
} from "model/Roster/Roster";
import { MatchTrack } from "model/Match/Track/MatchTrack";
import { TrackRound } from "model/Match/Track/TrackRound";
import {
  getRosterSeasonPoints,
  SeasonPointsRules,
} from "model/Tournament/Segment/Season/SeasonPointRules";
import { nonUndefined, onlyUnique } from "Utility";

export type CupModeSeasonRecord = {
  rosterId: string;
  matchesPlayed: number;
  positionPoints: number;
  matchPoints: ({ position: number; fastestTime: number } | undefined)[];
  position: number | undefined;
};

export type CupModeSeasonRecordWithDisplay = {
  record: CupModeSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createCupModeSeasonRecord = (
  rosterId: string,
  numberOfPlaydays: number
): CupModeSeasonRecord => {
  return {
    rosterId,
    matchesPlayed: 0,
    positionPoints: 0,
    matchPoints: Array(numberOfPlaydays).fill(undefined),
    position: undefined,
  };
};

export const calculateCupModeSeasonRecords = (
  seasonPoints: SeasonPointsRules | undefined,
  rosterIds: string[],
  matchesByPlayday: Match[][],
  numberOfPlaydays: number,
  forfeitRosterIds: string[]
) => {
  const records = new Map<string, CupModeSeasonRecord>();
  const rosterTotalFps = new Map<string, number>();
  rosterIds.forEach((id) => {
    records.set(id, createCupModeSeasonRecord(id, numberOfPlaydays));
  });

  for (var playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (var matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      if (!match.finalPositions) {
        continue;
      }

      const forfeitDriverIds = getDriverIdsFromRosterIds(
        match.teams.rosters,
        match.teams.scope,
        forfeitRosterIds
      );

      // Position points, matches played increment and total fps
      for (var rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const roster = getRoster(match.teams.rosters, rosterId);
        const rosterRecord = records.get(rosterId);
        const rosterFinalPos = match.finalPositions[rosterId];
        if (!roster || !rosterRecord || !rosterFinalPos) {
          continue;
        }

        rosterRecord.matchesPlayed++;
        const currentRosterMatchFpTotal = rosterTotalFps.get(rosterId) ?? 0;
        if (isForfeited(roster)) {
          rosterRecord.positionPoints += seasonPoints?.ffPoints ?? 0;
          rosterRecord.matchPoints[playdayI] = { position: 0, fastestTime: 0 };
          rosterTotalFps.set(rosterId, currentRosterMatchFpTotal + 4);
        } else {
          var positionPointsWithQual = getRosterSeasonPoints(
            rosterId,
            seasonPoints,
            match.finalPositions,
            match.scores ?? {},
            (_) => false,
            match.numWinners
          );
          var positionPoints =
            positionPointsWithQual === "Qual" ? 1000 : positionPointsWithQual;

          if (playdayI === 4 && (rosterId === "93" || rosterId === "149")) {
            positionPoints = 0;
          }

          rosterRecord.positionPoints += positionPoints;
          rosterRecord.matchPoints[playdayI] = {
            position: positionPoints,
            fastestTime: 0,
          };
          rosterTotalFps.set(
            rosterId,
            currentRosterMatchFpTotal + rosterFinalPos
          );
        }
      }

      // Fastest Time Points
      const matchTrackIds = nonUndefined(
        match.sets
          .flatMap((s) => s.tracks as MatchTrack[])
          .flatMap((t) => t.trackId)
      ).filter(onlyUnique);
      matchTrackIds.forEach((id) => {
        const fastestDriverId = getFastestTrackPointDriverId(
          match,
          id,
          forfeitDriverIds
        );

        const rosterId = !!fastestDriverId
          ? getRosterIdFromDriverId(
              fastestDriverId,
              match.teams.rosters,
              match.teams.scope
            )
          : undefined;
        if (!(playdayI === 4 && (rosterId === "93" || rosterId === "149"))) {
          if (!!rosterId) {
            const rosterRecord = records.get(rosterId);
            if (!!rosterRecord) {
              const playdayPoints = rosterRecord.matchPoints[playdayI];
              if (!playdayPoints) {
                rosterRecord.matchPoints[playdayI] = {
                  position: 0,
                  fastestTime: 1,
                };
              } else {
                playdayPoints.fastestTime++;
                rosterRecord.matchPoints[playdayI] = playdayPoints;
              }
            }
          }
        }
      });
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0) {
    return [];
  }

  if (sortedRecords[0].matchesPlayed > 0) {
    sortedRecords[0].position = 1;
    for (let i = 1; i < sortedRecords.length; i++) {
      if (sortedRecords[i].matchesPlayed > 0) {
        if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
          sortedRecords[i].position = sortedRecords[i - 1].position;
        } else {
          sortedRecords[i].position = i + 1;
        }
      }
    }
  }

  return sortedRecords;
};

const compareRecords = (
  a: CupModeSeasonRecord,
  b: CupModeSeasonRecord
): number => {
  if (a.positionPoints > b.positionPoints) {
    return -1;
  }

  if (b.positionPoints > a.positionPoints) {
    return 1;
  }

  return 0;
};

const getFastestTrackPointDriverId = (
  match: Match,
  trackId: string,
  forfeitDriverIds?: string[]
) => {
  const trackTimes = match.sets
    .flatMap((s) => s.tracks as MatchTrack[])
    .filter((t) => t.trackId === trackId)
    .flatMap((t) => t.rounds as TrackRound[])
    .flatMap((r) => r.times)
    .filter((t) => !forfeitDriverIds || !forfeitDriverIds.includes(t.driverId));
  var trackBestTime = Number.MAX_SAFE_INTEGER;
  var trackBestDriverId: string | undefined;
  for (var j = 0; j < trackTimes.length; j++) {
    const { finishTimeMs, driverId } = trackTimes[j];
    if (!!finishTimeMs && finishTimeMs > 0 && finishTimeMs < trackBestTime) {
      trackBestTime = finishTimeMs;
      trackBestDriverId = driverId;
    }
  }

  return trackBestDriverId;
};
